import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Loading from "@/components/Loading/Loading";
import api from "@/utils/axios";
import avatarPng from "@/assets/UI/avatar.png";
import cls from "./ClubMembers.module.scss";

const ALLOWED_STATES = new Set([
  "activeClubMember",
  "activeClubHost",
  "activeClubPartner",
]);

const safeStr = (v) => (v == null ? "" : String(v));
const hasText = (v) => safeStr(v).trim().length > 0;

const toTitle = (v) => {
  const s = safeStr(v).trim();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const ClubMembers = () => {
  const { t } = useTranslation();
  const { clubId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const [clubRes, membersRes] = await Promise.all([
          api.get(`/v1/lounge/clubs/${clubId}`),
          api.get(`/v1/lounge/clubs/${clubId}/members`),
        ]);

        if (ignore) return;

        const memberState = safeStr(clubRes?.data?.memberState);
        if (!ALLOWED_STATES.has(memberState)) {
          navigate(`/lounge/clubs/single/${clubId}`, { replace: true });
          return;
        }

        setPayload({
          memberState,
          club: clubRes?.data?.club || null,
          members: membersRes?.data?.members || [],
        });
      } catch (err) {
        if (ignore) return;
        setError(
          err?.response?.data?.message ||
            t("clubMembers.states.error", "Something went wrong."),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [clubId, navigate, t]);

  const club = payload?.club || null;
  const memberState = safeStr(payload?.memberState);
  const clubName =
    safeStr(club?.clubName) || t("clubHome.header.clubName", "Club");

  const clubNo = club?.clubNo != null ? String(club.clubNo) : "";
  const streetName = safeStr(club?.streetName);
  const cityName = safeStr(club?.cityName);

  const clubAddressParts = [
    hasText(clubNo) ? `${t("clubHome.header.clubNo", "Club")} ${clubNo}` : "",
    streetName,
    cityName,
  ].filter((x) => hasText(x));

  const clubAddress = clubAddressParts.length
    ? clubAddressParts.join(" · ")
    : t("clubHome.header.addressFallback", "Address not available");

  const clubAvatarSrc =
    club?.clubAvatarThumb?.url || club?.clubAvatar?.url || avatarPng;

  const showHostDash = memberState === "activeClubHost";
  const showPartnerDash = memberState === "activeClubPartner";

  const dashMeta = useMemo(() => {
    if (showHostDash) {
      return {
        label: t("clubHome.header.hostDashboard", "Host Dashboard"),
        to: `/lounge/clubs/single/${clubId}/host-dashboard`,
      };
    }

    if (showPartnerDash) {
      return {
        label: t("clubHome.header.partnerDashboard", "Partner Dashboard"),
        to: `/lounge/clubs/single/${clubId}/partner-dashboard`,
      };
    }

    return null;
  }, [clubId, showHostDash, showPartnerDash, t]);

  const clubBase = `/lounge/clubs/single/${clubId}`;
  const clubTabs = [
    {
      key: "feed",
      label: t("clubHome.tabs.feed", "Feed"),
      to: `${clubBase}/home`,
    },
    {
      key: "members",
      label: t("clubHome.tabs.members", "Members"),
      to: `${clubBase}/members`,
    },
    {
      key: "events",
      label: t("clubHome.tabs.events", "Events"),
      to: `${clubBase}/events`,
    },
    {
      key: "shop",
      label: t("clubHome.tabs.shop", "Shop"),
      to: `/lounge/marketplace/${clubId}`,
    },
  ];

  const isActiveTab = (to) => {
    const p = safeStr(location.pathname);
    if (p === to) return true;
    if (
      to.endsWith("/home") &&
      (p === clubBase || p.startsWith(`${clubBase}/home`))
    ) {
      return true;
    }
    return p.startsWith(to);
  };

  const memberCards = useMemo(() => {
    const members = Array.isArray(payload?.members) ? payload.members : [];

    return members.map((member, index) => {
      const avatar =
        member?.avatar?.url ||
        member?.avatarThumb?.url ||
        member?.profilePhoto?.url ||
        avatarPng;

      const username =
        safeStr(member?.displayName) ||
        safeStr(member?.username) ||
        safeStr(member?.name) ||
        t("clubMembers.memberFallback", "Unknown Member");

      const memberType =
        safeStr(member?.memberType).toLowerCase() === "couple"
          ? t("clubMembers.memberType.couple", "Couple")
          : t("clubMembers.memberType.single", "Single");

      const gender = hasText(member?.gender)
        ? toTitle(member?.gender)
        : t("clubMembers.unknown", "Unknown");

      const lookingFor = hasText(member?.lookingFor)
        ? toTitle(member?.lookingFor)
        : "";

      const city =
        safeStr(member?.cityName) ||
        safeStr(member?.city) ||
        safeStr(member?.location?.cityName) ||
        "";

      const country =
        safeStr(member?.countryName) ||
        safeStr(member?.country) ||
        safeStr(member?.location?.countryName) ||
        "";

      const about =
        safeStr(member?.aboutMe) ||
        safeStr(member?.bio) ||
        safeStr(member?.tagline) ||
        "";

      const joinedAt = safeStr(member?.createdAt);
      const memberLabel =
        member?.memberNo != null
          ? `#${String(member.memberNo).padStart(2, "0")}`
          : `#${String(index + 1).padStart(2, "0")}`;

      return {
        raw: member,
        avatar,
        username,
        memberType,
        gender,
        lookingFor,
        city,
        country,
        about,
        joinedAt,
        memberLabel,
      };
    });
  }, [payload?.members, t]);

  if (loading && !payload && !error) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      {/* Tabs row (keep as-is) */}
      <nav
        className={cls.tabs}
        aria-label={t("clubHome.topTabs.aria", "Lounge")}
      >
        <Link
          to="/lounge"
          className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.public", "Public")}
        </Link>
        <Link
          to="/lounge/friends-feed"
          className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.friends", "Friends")}
        </Link>
        <Link
          to="/lounge/clubs-feed"
          className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.clubs", "Clubs")}
        </Link>
        <Link
          to="/lounge/chat"
          className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
        >
          {t("clubHome.topTabs.chat", "Chat")}
        </Link>
        <Link
          to="/lounge/search"
          className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
        >
          🔍 {t("clubHome.topTabs.search", "Search")}
        </Link>
      </nav>

      {/* Header bar (avatar + name + actions) */}
      <header className={cls.header}>
        <div className={cls.headerMain}>
          <div className={cls.avatarWrap}>
            <img className={cls.avatar} src={clubAvatarSrc} alt={clubName} />
            <span className={cls.badgeOnline}>
              {t("clubHome.header.active", "Active")}
            </span>
          </div>

          <div className={cls.headerInfo}>
            <div className={cls.displayName}>{clubName}</div>

            <div className={cls.usernameRow}>
              <span className={cls.memberSince}>{clubAddress}</span>
            </div>

            <div className={cls.metaRow}>
              {dashMeta ? (
                <Link className={cls.btnPrimary} to={dashMeta.to}>
                  {dashMeta.label}
                </Link>
              ) : null}
            </div>

            {loading ? (
              <div className={cls.stateRow}>
                <span className={cls.stateDot} />
                <span className={cls.stateText}>
                  {t("clubHome.states.loading", "Loading club...")}
                </span>
              </div>
            ) : null}

            {!loading && error ? (
              <div className={cls.stateRowError}>
                <span className={cls.stateDot} />
                <span className={cls.stateText}>{error}</span>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {/* Tabs row (club sections) */}
      <nav
        className={cls.tabs}
        aria-label={t("clubHome.clubTabs.aria", "Club")}
      >
        {clubTabs.map((tab) => {
          const active = isActiveTab(tab.to);
          return (
            <Link
              key={tab.key}
              to={tab.to}
              className={`${cls.tabButton} ${active ? cls.activeTab : ""}`}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>

      {/* Center-only content (no left column) */}
      <div className={cls.centerCol}>
        <section className={cls.membersHero}>
          <div className={cls.membersHeroTop}>
            <div>
              <div className={cls.heroEyebrow}>
                {t("clubMembers.hero.eyebrow", "Club Roster")}
              </div>
              <h1 className={cls.membersTitle}>
                {t("clubMembers.title", "Club Members")}
              </h1>
              <p className={cls.membersSub}>
                {t(
                  "clubMembers.subtitle",
                  "See who is inside the club and explore member profiles.",
                )}
              </p>
            </div>

            <div className={cls.totalBadge}>
              <span className={cls.totalBadgeLabel}>
                {t("clubMembers.total", "Total")}
              </span>
              <strong className={cls.totalBadgeValue}>
                {memberCards.length}
              </strong>
            </div>
          </div>

          <div className={cls.statGrid}>
            <div className={cls.statCard}>
              <span className={cls.statLabel}>
                {t("clubMembers.stats.all", "All Members")}
              </span>
              <strong className={cls.statValue}>{memberCards.length}</strong>
            </div>

            <div className={cls.statCard}>
              <span className={cls.statLabel}>
                {t("clubMembers.stats.singles", "Singles")}
              </span>
              <strong className={cls.statValue}>
                {
                  memberCards.filter(
                    (member) =>
                      safeStr(member.raw?.memberType).toLowerCase() !==
                      "couple",
                  ).length
                }
              </strong>
            </div>

            <div className={cls.statCard}>
              <span className={cls.statLabel}>
                {t("clubMembers.stats.couples", "Couples")}
              </span>
              <strong className={cls.statValue}>
                {
                  memberCards.filter(
                    (member) =>
                      safeStr(member.raw?.memberType).toLowerCase() ===
                      "couple",
                  ).length
                }
              </strong>
            </div>
          </div>
        </section>

        <section className={cls.membersWrap}>
          {error ? (
            <div className={cls.feedStateBad}>{error}</div>
          ) : memberCards.length === 0 ? (
            <div className={cls.feedState}>
              {t("clubMembers.empty", "No members found yet.")}
            </div>
          ) : (
            <div className={cls.memberGrid}>
              {memberCards.map((member) => (
                <article
                  key={`${member.raw?.userId || member.username}-${member.raw?.memberId || "x"}`}
                  className={cls.memberCard}
                >
                  <div className={cls.memberGlow} />

                  <div className={cls.memberTop}>
                    <div className={cls.memberAvatarWrap}>
                      <img
                        src={member.avatar}
                        alt={member.username}
                        className={cls.memberAvatar}
                      />
                      <span className={cls.memberIndex}>
                        {member.memberLabel}
                      </span>
                    </div>

                    <div className={cls.memberTopInfo}>
                      <h3 className={cls.memberName}>{member.username}</h3>
                      <div className={cls.memberTagRow}>
                        <span className={`${cls.memberTag} ${cls.tagPrimary}`}>
                          {member.memberType}
                        </span>
                        <span className={`${cls.memberTag} ${cls.tagSoft}`}>
                          {member.gender}
                        </span>
                        {hasText(member.lookingFor) ? (
                          <span className={`${cls.memberTag} ${cls.tagSoft}`}>
                            {member.lookingFor}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* <div className={cls.memberDetails}>
                    <div className={cls.detailBox}>
                      <span className={cls.detailLabel}>
                        {t("clubMembers.fields.city", "City")}
                      </span>
                      <span className={cls.detailValue}>
                        {hasText(member.city)
                          ? member.city
                          : t("clubMembers.notAvailable", "Not available")}
                      </span>
                    </div>

                    <div className={cls.detailBox}>
                      <span className={cls.detailLabel}>
                        {t("clubMembers.fields.country", "Country")}
                      </span>
                      <span className={cls.detailValue}>
                        {hasText(member.country)
                          ? member.country
                          : t("clubMembers.notAvailable", "Not available")}
                      </span>
                    </div>
                  </div> */}

                  {/* <div className={cls.aboutBox}>
                    <div className={cls.aboutLabel}>
                      {t("clubMembers.fields.about", "About")}
                    </div>
                    <p className={cls.aboutText}>
                      {hasText(member.about)
                        ? member.about
                        : t(
                            "clubMembers.aboutFallback",
                            "This member has not added details yet.",
                          )}
                    </p>
                  </div> */}

                  <div className={cls.memberActions}>
                    {member.raw?.memberId ? (
                      <Link
                        className={cls.viewBtn}
                        to={`/lounge/member-profile/${member.raw.memberId}`}
                      >
                        {t("clubMembers.viewProfile", "View Profile")}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className={cls.viewBtnDisabled}
                        disabled
                      >
                        {t("clubMembers.viewProfile", "View Profile")}
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ClubMembers;
